import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "@/components/Container";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";
import { safeImg } from "@/lib/safeImg";
import { PUBLIC } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";

const FALLBACK_BEFORE = "https://images.pexels.com/photos/15535241/pexels-photo-15535241.jpeg";
const FALLBACK_AFTER  = "https://images.unsplash.com/photo-1748063578185-3d68121b11ff";

export default function BeforeAfter() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/public/case-studies").then(r => setItems(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="pt-36 pb-32">
      <Container>
        <div className="flex items-center gap-4 mb-10">
          <span className="label-mono">No. 05 — Transformations</span>
          <span className="h-px w-12 bg-ruby" />
        </div>
        <h1 className="serif-display text-5xl md:text-7xl lg:text-[8rem] text-ink-700">
          Before<br />
          <span className="serif-italic text-ruby">& after.</span>
        </h1>
        <p className="mt-10 text-ink-400 max-w-2xl text-lg leading-[1.7]">
          Drag the slider on each project to see the Rubys.Group transformation — every brief, every brick, every brushstroke.
        </p>
      </Container>

      <Container className="mt-24 space-y-32 lg:space-y-40">
        {items.length === 0 && (
          <div className="border-t-2 border-ruby pt-12">
            <p className="label-mono">Featured</p>
            <h2 className="serif-display text-4xl md:text-5xl text-ink-700 mt-4">A signature transformation.</h2>
            <div className="mt-10">
              <BeforeAfterSlider before={FALLBACK_BEFORE} after={FALLBACK_AFTER} tone="light" />
            </div>
            <p className="mt-10 text-ink-400 max-w-xl leading-[1.8]">
              Our first publicly published case studies will appear here as projects complete. Register interest below for first viewings.
            </p>
          </div>
        )}
        {items.map((s, idx) => (
          <article key={s.id} data-testid={PUBLIC.caseStudyCard(s.id)} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
            <div className="lg:col-span-8">
              <BeforeAfterSlider
                before={safeImg((s.slider_pairs?.[0]?.before) || s.before_images?.[0], FALLBACK_BEFORE)}
                after={safeImg((s.slider_pairs?.[0]?.after) || s.after_images?.[0], FALLBACK_AFTER)}
                tone="light"
              />
            </div>
            <div className="lg:col-span-4">
              <p className="label-mono-mute">{s.location}</p>
              <h2 className="mt-5 serif-display text-4xl lg:text-5xl text-ink-700">{s.project_title}</h2>
              {s.short_story && <p className="mt-6 text-ink-400 leading-[1.8] line-clamp-5">{s.short_story}</p>}
              <Link to={`/before-after/${s.id}`} className="btn-link mt-8 inline-flex">
                View case study <ArrowUpRight size={14} />
              </Link>
            </div>
          </article>
        ))}
      </Container>
    </div>
  );
}
